<!-- Dynamic Data-Driven Inspector -->
<script lang="ts">
  import {
    sceneState,
    expandAllComponents,
    collapseAllComponents,
  } from '../stores/sceneEditorStore';
  import { generateSchemaFromObject } from '../metadata/schemas';
  import ComponentEditor from './ComponentEditor.svelte';
  import FiltersEditor from './FiltersEditor.svelte';
  import Icon from '@/ui/base/Icon.svelte';
  import { logger } from '@/engine/logging';
  import type { SpriteObject } from '../types';

  // Реактивно получаем выбранный объект
  $: selectedObject = $sceneState.selectedObjectId
    ? $sceneState.objects[$sceneState.selectedObjectId]
    : null;

  // Debug: проверяем что объект есть
  $: {
    logger.info('🔍 Inspector state changed', {
      source: 'inspector',
      selectedObjectId: $sceneState.selectedObjectId,
      hasSelectedObject: !!selectedObject,
      selectedObjectKeys: selectedObject ? Object.keys(selectedObject) : [],
      selectedObject: selectedObject,
    });
  }

  // Автоматически генерируем схему из объекта
  $: components = selectedObject ? generateSchemaFromObject(selectedObject) : [];

  // Debug logging
  $: {
    logger.info('🔍 Inspector generated schema', {
      source: 'inspector',
      objectId: selectedObject?.id,
      objectType: selectedObject?.type,
      componentsCount: components.length,
      components: components.map(c => ({
        name: c.displayName,
        propertiesCount: c.properties.length,
        properties: c.properties.map(p => `${p.label} (${p.type})`),
      })),
    });
  }
</script>

<div class="inspector h-full flex flex-col bg-gray-900">
  <!-- Header -->
  <div class="p-3 border-b border-gray-700">
    <div class="flex items-center justify-between mb-1">
      <h3 class="text-base font-semibold text-white">Inspector</h3>

      {#if selectedObject && components.length > 0}
        <div class="flex gap-1">
          <button
            class="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
            onclick={() => expandAllComponents(selectedObject.id)}
            title="Expand all components"
          >
            <Icon name="expand" size="sm" class="mr-1" />
            Expand All
          </button>
          <button
            class="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
            onclick={() => collapseAllComponents(selectedObject.id)}
            title="Collapse all components"
          >
            <Icon name="minus" size="sm" class="mr-1" />
            Collapse All
          </button>
        </div>
      {/if}
    </div>

    {#if selectedObject}
      <div class="flex items-center gap-2 text-xs text-gray-300">
        <Icon name={selectedObject.type === 'sprite' ? 'sprite' : 'space-bg'} size="sm" />
        <span>{selectedObject.name}</span>
        <span class="text-gray-500">({selectedObject.type})</span>
      </div>
    {:else}
      <div class="text-xs text-gray-500">No object selected</div>
    {/if}
  </div>

  <!-- Properties (auto-generated from object data!) -->
  <div class="flex-1 overflow-y-auto p-3 space-y-4" onwheel={e => e.stopPropagation()}>
    {#if selectedObject}
      {#if components.length > 0}
        {#each components as component (component.name)}
          <ComponentEditor objectId={selectedObject.id} object={selectedObject} {component} />
        {/each}

        <!-- Filters section (for sprites) -->
        {#if selectedObject.type === 'sprite'}
          {@const spriteObject = selectedObject as SpriteObject}
          <FiltersEditor objectId={spriteObject.id} filters={spriteObject.filters || []} />
        {/if}
      {:else}
        <!-- Debug: No components generated -->
        <div class="text-center text-yellow-500 py-6">
          <p class="text-xs">⚠️ Selected object has no visible properties</p>
          <p class="text-xs mt-1 text-gray-600">Object ID: {selectedObject.id}</p>
          <p class="text-xs mt-1 text-gray-600">Object Type: {selectedObject.type}</p>
          <p class="text-xs mt-1 text-gray-600">Keys: {Object.keys(selectedObject).join(', ')}</p>
        </div>
      {/if}
    {:else}
      <div class="text-center text-gray-500 py-6">
        <Icon name="inspect" size="lg" class="mx-auto mb-2" />
        <p class="text-xs">Select an object to inspect its properties</p>
        <p class="text-xs mt-1 text-gray-600">Properties will be auto-generated</p>
      </div>
    {/if}
  </div>
</div>

<style>
  .inspector {
    /* Prevent text selection during drag */
    user-select: none;
    -webkit-user-select: none;
  }
</style>
